import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Delete } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, IconButton, Tooltip, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import GRDialogTitle from '../../../components/GRDialogTitle/GRDialogTitle'
import { GRTable, GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import { isFetchBaseQueryError } from '../../../helpers/isFetchBaseQueryError'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import {
  useDeleteInternalOrphanedUserMutation,
  useGetInternalOrganizationsQuery,
  useGetInternalOrphanedUsersQuery,
  useRestoreInternalOrphanedUserMutation,
} from '../../api/organizations'
import { getUserDisplayName } from '../../features/organizations/components/helpers/organizationUserHelpers'
import { InternalOrganization, OrphanedUser } from '../../features/organizations/types/Organizations'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { QueryStateBoundary } from '../QueryStateBoundary/QueryStateBoundary'

type OrphanedUsersDialogProps = {
  open: boolean
  onClose: () => void
}

type CustomProps = {
  organizationsById: Map<string, InternalOrganization>
  onRestore: (user: OrphanedUser, organization: InternalOrganization) => void
  onDelete: (user: OrphanedUser) => void
}

type PendingAction = { kind: 'restore'; user: OrphanedUser; organization: InternalOrganization } | { kind: 'delete'; user: OrphanedUser }

const nameOrDash = (value: string | null) => value || '---'

/** Users removed from their organization: restore one to it or delete it permanently. Shared by Organizations and Users. */
export const OrphanedUsersDialog: FC<OrphanedUsersDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation()
  const { notify } = useMutationFeedback()
  const orphansQuery = useGetInternalOrphanedUsersQuery(undefined, { skip: !open })
  const organizationsQuery = useGetInternalOrganizationsQuery(undefined, { skip: !open })
  const [restoreUser] = useRestoreInternalOrphanedUserMutation()
  const [deleteUser] = useDeleteInternalOrphanedUserMutation()
  const [confirm, setConfirm] = useState<ConfirmDialogData<PendingAction>>()

  const organizationsById = useMemo(
    () => new Map((organizationsQuery.data ?? []).map((organization) => [organization.id, organization])),
    [organizationsQuery.data]
  )
  const orphans = useMemo(() => orphansQuery.data ?? [], [orphansQuery.data])

  const previousOrganizationName = useCallback(
    (user: OrphanedUser, byId: Map<string, InternalOrganization>) => {
      const organization = user.removedFrom ? byId.get(user.removedFrom) : undefined
      return organization ? organization.name || organization.id : t('internal-csm:orphaned_users_unknown_organization')
    },
    [t]
  )

  const tableColumns: GRTableColumn<OrphanedUser, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:edit_user_email'),
        accessor: ({ row }) => row.email,
        sortable: true,
        sortAccessor: ({ row }) => row.email.toLowerCase(),
        sortOrder: SortOrder.DESC,
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left', sx: { minWidth: 220 } },
      },
      {
        labelAccessor: t('internal-csm:edit_user_first_name'),
        accessor: ({ row }) => nameOrDash(row.firstName),
        sortable: true,
        sortAccessor: ({ row }) => (row.firstName ?? '').toLowerCase(),
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: t('internal-csm:edit_user_last_name'),
        accessor: ({ row }) => nameOrDash(row.lastName),
        sortable: true,
        sortAccessor: ({ row }) => (row.lastName ?? '').toLowerCase(),
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: t('internal-csm:orphaned_users_previous_organization'),
        accessor: ({ row, customTableProps }) => {
          const known = !!row.removedFrom && !!customTableProps?.organizationsById.has(row.removedFrom)
          return (
            <Typography variant="body2" color={known ? undefined : 'text.secondary'}>
              {customTableProps ? previousOrganizationName(row, customTableProps.organizationsById) : ''}
            </Typography>
          )
        },
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => (customTableProps ? previousOrganizationName(row, customTableProps.organizationsById).toLowerCase() : ''),
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => {
          const organization = row.removedFrom ? customTableProps?.organizationsById.get(row.removedFrom) : undefined
          return (
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
              <Tooltip title={organization ? '' : t('internal-csm:orphaned_users_restore_unknown')}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!organization}
                    onClick={() => organization && customTableProps?.onRestore(row, organization)}
                  >
                    {t('internal-csm:orphaned_users_restore')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={t('internal-csm:orphaned_users_delete')}>
                <IconButton size="small" color="error" aria-label={t('internal-csm:orphaned_users_delete')} onClick={() => customTableProps?.onDelete(row)}>
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          )
        },
        cellProps: { align: 'right', sx: { px: 1 } },
        headerCellProps: { sx: { minWidth: 150 } },
      },
    ],
    [t, previousOrganizationName]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      organizationsById,
      onRestore: (user, organization) =>
        setConfirm({
          title: t('internal-csm:orphaned_users_restore'),
          confirmText: t('internal-csm:orphaned_users_restore_confirm', { name: getUserDisplayName(user), organization: organization.name || organization.id }),
          actionText: t('internal-csm:orphaned_users_restore'),
          data: { kind: 'restore', user, organization },
        }),
      onDelete: (user) =>
        setConfirm({
          title: t('internal-csm:orphaned_users_delete'),
          confirmText: t('internal-csm:orphaned_users_delete_confirm', { name: getUserDisplayName(user), email: user.email }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: { kind: 'delete', user },
        }),
    }),
    [organizationsById, t]
  )

  const handleConfirm = async (confirmed?: ConfirmDialogData<PendingAction>) => {
    setConfirm(undefined)
    const action = confirmed?.data
    if (!action) return
    const { user } = action
    try {
      if (action.kind === 'restore') {
        const organizationName = action.organization.name || action.organization.id
        const { stored } = await restoreUser({ organizationId: action.organization.id, username: user.username }).unwrap()
        if (stored) notify(t('internal-csm:orphaned_users_not_restored', { email: user.email }), 'error')
        else notify(t('internal-csm:orphaned_users_restored', { email: user.email, organization: organizationName }), 'success')
      } else {
        const { stored } = await deleteUser(user.username).unwrap()
        if (stored) notify(t('internal-csm:orphaned_users_not_deleted', { email: user.email }), 'error')
        else notify(t('internal-csm:orphaned_users_deleted', { email: user.email }), 'success')
      }
    } catch (error) {
      if (action.kind === 'restore' && isFetchBaseQueryError(error) && error.status === 409) {
        notify(t('internal-csm:orphaned_users_no_seats'), 'error')
        return
      }
      const key = action.kind === 'restore' ? 'internal-csm:orphaned_users_restore_failed' : 'internal-csm:orphaned_users_delete_failed'
      const reason = getValidationErrorMessage(error)
      notify(reason ? `${t(key)}: ${reason}` : t(key), 'error')
    }
  }

  const isLoading = orphansQuery.isLoading || organizationsQuery.isLoading
  const isError = orphansQuery.isError || organizationsQuery.isError

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <GRDialogTitle onClose={onClose}>{t('internal-csm:orphaned_users_title', { count: orphans.length })}</GRDialogTitle>
      <DialogContent>
        <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-csm:orphaned_users_load_failed')}>
          {orphans.length ? (
            <GRTable
              rows={orphans}
              columns={columns}
              onColumnsUpdated={setColumns}
              rowIdKey="id"
              customProps={customProps}
              noRowsLabel={t('internal-common:no_rows')}
              striped
              hoverable
            />
          ) : (
            <Typography>{t('internal-csm:orphaned_users_none')}</Typography>
          )}
        </QueryStateBoundary>
      </DialogContent>
      <ConfirmDialog open={!!confirm} confirmDialogData={confirm} onClose={handleConfirm} />
    </Dialog>
  )
}
